import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiMaximize2, FiFlag, FiHeart } from 'react-icons/fi';
import supabase from '../supabase/supabase';

export default function PostCard({ item, onImageClick, onReport }) {
  const [votes, setVotes] = useState(item.vote_count || 0);
  const [isLiking, setIsLiking] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);

  // Check if this machine has already voted for this post on mount
  useEffect(() => {
    const votedPosts = JSON.parse(localStorage.getItem('yoopost_voted_ids') || '[]');
    if (votedPosts.includes(item.id)) {
      setHasVoted(true);
    }
  }, [item.id]);

  const handleLike = async (e) => {
    e.stopPropagation();
    
    // Prevent double voting
    if (isLiking || hasVoted) return;

    setIsLiking(true);
    const newVoteCount = votes + 1;
    
    // Optimistic update
    setVotes(newVoteCount);
    setHasVoted(true);

    try {
      // 1. Update the database
      const { error } = await supabase
        .from('posts_1715000000')
        .update({ vote_count: newVoteCount })
        .eq('id', item.id);

      if (error) {
        throw error;
      }

      // 2. Persist the vote locally to this machine
      const votedPosts = JSON.parse(localStorage.getItem('yoopost_voted_ids') || '[]');
      if (!votedPosts.includes(item.id)) {
        votedPosts.push(item.id);
        localStorage.setItem('yoopost_voted_ids', JSON.stringify(votedPosts));
      }
    } catch (err) {
      console.error("Error updating votes:", err);
      // Revert states on failure
      setVotes(votes);
      setHasVoted(false);
    } finally {
      setIsLiking(false);
    }
  };

  return (
    <div className="relative group mb-8 break-inside-avoid bg-white border-4 border-retro-brown rounded-2xl shadow-retro hover:shadow-retro-hover transition-all duration-300 overflow-hidden hover:-translate-y-1">
      <div 
        className="relative overflow-hidden cursor-zoom-in bg-gray-100 border-b-4 border-retro-brown"
        onClick={() => onImageClick(item)}
      >
        <img 
          src={item.image_url} 
          alt={item.title || "Portfolio Graphic"} 
          className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          onContextMenu={(e) => e.preventDefault()}
          draggable="false"
          style={{ WebkitUserDrag: 'none', userSelect: 'none' }}
        />
        
        <div className="absolute inset-0 bg-retro-brown/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-between p-4">
          <div className="flex justify-between items-start">
            <div className="p-2 bg-retro-cream border-2 border-retro-brown shadow-retro-sm rounded-full text-retro-brown opacity-0 group-hover:opacity-100 transition-all pointer-events-none">
              <SafeIcon icon={FiMaximize2} className="text-lg font-bold" />
            </div>
          </div>
          
          <div className="flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 delay-100">
            <div>
              {item.client && (
                <div className="bg-retro-yellow border-2 border-retro-brown shadow-retro-sm text-retro-brown text-xs font-bold py-1.5 px-3 rounded-full flex items-center gap-1 truncate max-w-[140px] pointer-events-none">
                  <span className="truncate">{item.client.toUpperCase()}</span>
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <button 
                onClick={(e) => { e.stopPropagation(); onReport(item); }}
                title="Report Listing"
                className="w-10 h-10 rounded-full bg-red-100 border-2 border-retro-brown shadow-retro-sm hover:shadow-retro hover:bg-red-500 hover:text-white active:translate-y-1 active:shadow-retro-active flex items-center justify-center text-red-600 transition-all"
              >
                <SafeIcon icon={FiFlag} className="text-lg font-bold" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="p-4 bg-retro-cream">
        <div className="flex justify-between items-center gap-2 mb-3">
          <div className="flex-1">
            {item.client && <p className="text-[12px] text-retro-brown/60 font-bold uppercase tracking-wider">{item.client}</p>}
          </div>
          
          <button 
            onClick={handleLike}
            disabled={hasVoted || isLiking}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full border-2 border-retro-brown shadow-retro-sm transition-all 
              ${hasVoted 
                ? 'bg-red-50 cursor-default opacity-90' 
                : 'bg-white hover:shadow-retro hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-retro-active cursor-pointer'
              } 
              ${isLiking ? 'scale-110' : ''}`}
            title={hasVoted ? "Already upvoted!" : "Upvote this graphic"}
          >
            <SafeIcon 
              icon={FiHeart} 
              className={`text-lg transition-colors ${hasVoted || votes > 0 ? 'fill-red-500 text-red-500' : 'text-retro-brown'}`} 
            />
            <span className={`font-bold text-sm ${hasVoted ? 'text-red-600' : 'text-retro-brown'}`}>{votes}</span>
          </button>
        </div>

        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {item.tags.map((tag, i) => {
              const colors = ['bg-retro-teal text-white', 'bg-retro-yellow text-retro-brown', 'bg-retro-lime text-retro-brown'];
              const colorClass = colors[i % colors.length];
              return (
                <span key={i} className={`text-[11px] font-bold ${colorClass} border-2 border-retro-brown px-2 py-0.5 rounded-full shadow-retro-sm`}>
                  #{tag.toUpperCase()}
                </span>
              )
            })}
          </div>
        )}
      </div>
    </div>
  );
}